/**
 * @file API service — dropdownOptionsPublicService.
 * Load options for a CMS dropdown by id (used when form fields omit embedded options).
 */

import axiosClient from "../api/axiosClient";

/** Type definition — Cms Dropdown Option Row. */
export type CmsDropdownOptionRow = {
  id?: number;
  value: string;
  label: string;
};

function extractOptionsFromPayload(payload: unknown): CmsDropdownOptionRow[] {
  if (!payload) return [];
  const p = payload as Record<string, unknown>;
  if (Array.isArray(payload)) {
    return normalizeOptionRows(payload as Record<string, unknown>[]);
  }
  if (Array.isArray(p.options)) {
    return normalizeOptionRows(p.options as Record<string, unknown>[]);
  }
  const data = p.data as Record<string, unknown> | undefined;
  if (data && Array.isArray(data.options)) {
    return normalizeOptionRows(data.options as Record<string, unknown>[]);
  }
  if (data && Array.isArray(data)) {
    return normalizeOptionRows(data as Record<string, unknown>[]);
  }
  if (Array.isArray(p.data)) {
    return normalizeOptionRows(p.data as Record<string, unknown>[]);
  }
  return [];
}

function normalizeOptionRows(
  rows: Record<string, unknown>[]
): CmsDropdownOptionRow[] {
  return rows
    .map((o) => {
      const id = o.id != null ? Number(o.id) : undefined;
      const valueRaw = o.value ?? o.id;
      const value = String(valueRaw ?? "").trim();
      const label = String(o.label ?? o.value ?? o.id ?? "").trim();
      return { id: Number.isFinite(id) ? id : undefined, value, label };
    })
    .filter((o) => o.value && o.label);
}

/** Exported function — fetch Dropdown Options By Id. */
export async function fetchDropdownOptionsById(
  dropdownId: number
): Promise<CmsDropdownOptionRow[]> {
  if (!Number.isFinite(dropdownId) || dropdownId <= 0) return [];
  try {
    const res = await axiosClient.get(`/dropdowns/${dropdownId}`);
    return extractOptionsFromPayload(res?.data?.data ?? res?.data);
  } catch {
    return [];
  }
}
